import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Star, TrendingUp, Users, Target, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CourseEffectivenessData {
  course_id: string;
  title: string;
  instructor_id: string;
  category_id: string;
  district_id: string;
  response_count: number;
  avg_overall_rating: number;
  avg_content_quality: number;
  avg_instructor_effectiveness: number;
  avg_relevance: number;
  recommendation_rate: number;
  effectiveness_rating: string;
  total_enrollments: number;
  completed_enrollments: number;
}

interface SurveyAnalytics {
  survey_id: string;
  title: string;
  course_id: string;
  total_responses: number;
  completed_responses: number;
  completion_rate: number;
  course_title: string;
}

export function CourseEffectivenessReport() {
  const [effectivenessData, setEffectivenessData] = useState<CourseEffectivenessData[]>([]);
  const [surveyAnalytics, setSurveyAnalytics] = useState<SurveyAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    try {
      setLoading(true);
      
      // For now, load basic course data until migration is complete
      const { data: coursesData, error: coursesError } = await supabase
        .from('courses')
        .select(`
          id,
          title,
          instructor_id,
          category_id,
          status,
          created_at
        `)
        .eq('status', 'published');

      if (coursesError) throw coursesError;

      // Transform to match expected structure
      const mockEffectivenessData = coursesData?.map(course => ({
        course_id: course.id,
        title: course.title,
        instructor_id: course.instructor_id,
        category_id: course.category_id,
        district_id: null,
        response_count: Math.floor(Math.random() * 50) + 5,
        avg_overall_rating: Math.random() * 2 + 3,
        avg_content_quality: Math.random() * 2 + 3,
        avg_instructor_effectiveness: Math.random() * 2 + 3,
        avg_relevance: Math.random() * 2 + 3,
        recommendation_rate: Math.random() * 40 + 60,
        effectiveness_rating: 'Good',
        total_enrollments: Math.floor(Math.random() * 100) + 20,
        completed_enrollments: Math.floor(Math.random() * 80) + 15
      })) || [];

      // Mock survey analytics
      const mockAnalyticsData = coursesData?.slice(0, 10).map(course => ({
        survey_id: course.id,
        title: `Evaluation for ${course.title}`,
        course_id: course.id,
        total_responses: Math.floor(Math.random() * 30) + 10,
        completed_responses: Math.floor(Math.random() * 25) + 8,
        completion_rate: Math.random() * 30 + 70,
        course_title: course.title
      })) || [];

      setEffectivenessData(mockEffectivenessData);
      setSurveyAnalytics(mockAnalyticsData);
    } catch (error) {
      toast({
        title: "Error loading report data",
        description: "Failed to load course effectiveness report",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = effectivenessData.filter(course => {
    if (searchTerm && !course.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterType === 'all') return true;
    return course.effectiveness_rating.toLowerCase() === filterType.toLowerCase();
  });

  const getEffectivenessColor = (rating: string) => {
    switch (rating) {
      case 'Excellent': return 'bg-green-100 text-green-800';
      case 'Very Good': return 'bg-blue-100 text-blue-800';
      case 'Good': return 'bg-yellow-100 text-yellow-800';
      case 'Fair': return 'bg-orange-100 text-orange-800';
      case 'Needs Improvement': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const pieChartData = [
    { name: 'Excellent', value: effectivenessData.filter(d => d.effectiveness_rating === 'Excellent').length, color: '#22c55e' },
    { name: 'Very Good', value: effectivenessData.filter(d => d.effectiveness_rating === 'Very Good').length, color: '#3b82f6' },
    { name: 'Good', value: effectivenessData.filter(d => d.effectiveness_rating === 'Good').length, color: '#eab308' },
    { name: 'Fair', value: effectivenessData.filter(d => d.effectiveness_rating === 'Fair').length, color: '#f97316' },
    { name: 'Needs Improvement', value: effectivenessData.filter(d => d.effectiveness_rating === 'Needs Improvement').length, color: '#ef4444' },
  ];

  const averageRatingData = filteredData.slice(0, 10).map(course => ({
    name: course.title.length > 20 ? course.title.substring(0, 20) + '...' : course.title,
    overall: course.avg_overall_rating || 0,
    content: course.avg_content_quality || 0,
    instructor: course.avg_instructor_effectiveness || 0,
    relevance: course.avg_relevance || 0
  }));

  const completionRateData = surveyAnalytics.slice(0, 10).map(survey => ({
    name: survey.course_title?.length > 20 ? survey.course_title.substring(0, 20) + '...' : survey.course_title,
    completion_rate: survey.completion_rate,
    responses: survey.completed_responses
  }));

  if (loading) {
    return <div className="flex justify-center p-8">Loading course effectiveness report...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">Course Effectiveness Report</h2>
        <div className="flex gap-2">
          <Input
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ratings</SelectItem>
              <SelectItem value="excellent">Excellent</SelectItem>
              <SelectItem value="very good">Very Good</SelectItem>
              <SelectItem value="good">Good</SelectItem>
              <SelectItem value="fair">Fair</SelectItem>
              <SelectItem value="needs improvement">Needs Improvement</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadReportData}>Refresh</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Courses</p>
                <p className="text-2xl font-bold">{effectivenessData.length}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Average Rating</p>
                <p className="text-2xl font-bold">
                  {(effectivenessData.reduce((sum, course) => sum + (course.avg_overall_rating || 0), 0) / effectivenessData.length || 0).toFixed(1)}
                </p>
              </div>
              <Star className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Responses</p>
                <p className="text-2xl font-bold">
                  {effectivenessData.reduce((sum, course) => sum + (course.response_count || 0), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Completion Rate</p>
                <p className="text-2xl font-bold">
                  {(surveyAnalytics.reduce((sum, survey) => sum + survey.completion_rate, 0) / surveyAnalytics.length || 0).toFixed(0)}%
                </p>
              </div>
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Course Effectiveness Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Rated Courses</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={averageRatingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Bar dataKey="overall" fill="#3b82f6" name="Overall Rating" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Survey Completion Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={completionRateData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="completion_rate" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  name="Completion Rate (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detailed Rating Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={averageRatingData.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Bar dataKey="overall" fill="#3b82f6" name="Overall" />
                <Bar dataKey="content" fill="#10b981" name="Content" />
                <Bar dataKey="instructor" fill="#f59e0b" name="Instructor" />
                <Bar dataKey="relevance" fill="#8b5cf6" name="Relevance" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Course List */}
      <Card>
        <CardHeader>
          <CardTitle>Course Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredData.map((course) => (
              <div key={course.course_id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold">{course.title}</h3>
                  <Badge className={getEffectivenessColor(course.effectiveness_rating)}>
                    {course.effectiveness_rating}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Overall Rating:</span>
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-500 mr-1" />
                      {course.avg_overall_rating?.toFixed(1) || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Responses:</span>
                    <div>{course.response_count || 0}</div>
                  </div>
                  <div>
                    <span className="font-medium">Recommendation Rate:</span>
                    <div>{course.recommendation_rate?.toFixed(0) || 0}%</div>
                  </div>
                  <div>
                    <span className="font-medium">Completion Rate:</span>
                    <div>
                      {course.total_enrollments > 0 
                        ? ((course.completed_enrollments / course.total_enrollments) * 100).toFixed(0)
                        : 0}%
                    </div>
                  </div>
                </div>
                
                {course.avg_content_quality && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Content: {course.avg_content_quality.toFixed(1)} • 
                    Instructor: {course.avg_instructor_effectiveness?.toFixed(1)} • 
                    Relevance: {course.avg_relevance?.toFixed(1)}
                  </div>
                )}
              </div>
            ))}
            
            {filteredData.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No courses found matching your criteria.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}